import React from "react";
import { MdClose, MdSearch } from "react-icons/md";
import { UI_DROPDOWN_MENU, UI_SELECT } from "lib/uiClasses";

export type MultiSelectOption = {
  value: number | string;
  label: string;
  description?: string;
};

export type MultiSelectProps = {
  value: Array<number | string>;
  options: MultiSelectOption[];
  onChange: (next: Array<number | string>) => void;
  placeholder?: string;
  /** Optional controlled search — set if caller wants remote filtering. */
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  loading?: boolean;
  className?: string;
  /** Max number of chips rendered before "+N more". */
  maxChips?: number;
};

/**
 * Searchable chip multi-select. Designed for light admin forms; supports
 * both local (auto-filtered by label) and remote (onSearchChange) modes.
 */
export default function MultiSelect({
  value,
  options,
  onChange,
  placeholder,
  searchTerm,
  onSearchChange,
  loading,
  className = "",
  maxChips = 5,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [localTerm, setLocalTerm] = React.useState("");
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const effectiveTerm = searchTerm ?? localTerm;
  const byValue = React.useMemo(() => {
    const map = new Map<string, MultiSelectOption>();
    for (const o of options) map.set(String(o.value), o);
    return map;
  }, [options]);

  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filteredOptions = React.useMemo(() => {
    // When controlled remotely the options are already filtered.
    if (onSearchChange) return options;
    const term = effectiveTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => o.label.toLowerCase().includes(term));
  }, [options, effectiveTerm, onSearchChange]);

  const toggle = (val: number | string) => {
    const key = String(val);
    if (value.map(String).includes(key)) {
      onChange(value.filter((v) => String(v) !== key));
    } else {
      onChange([...value, val]);
    }
  };

  const selectedChips = value.slice(0, maxChips).map((v) => {
    const opt = byValue.get(String(v));
    return (
      <span
        key={String(v)}
        className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-400/10 dark:text-brand-200"
      >
        {opt?.label ?? String(v)}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle(v);
          }}
          className="text-brand-500 hover:text-brand-700 dark:text-brand-200"
          aria-label="Remove"
        >
          <MdClose className="h-3 w-3" />
        </button>
      </span>
    );
  });

  return (
    <div ref={containerRef} className={`relative ${className}`.trim()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${UI_SELECT} flex min-h-[42px] flex-wrap items-center gap-1 py-1.5 text-start shadow-sm`}
      >
        {value.length === 0 ? (
          <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
        ) : (
          <>
            {selectedChips}
            {value.length > maxChips ? (
              <span className="text-xs text-gray-500">+{value.length - maxChips}</span>
            ) : null}
          </>
        )}
      </button>
      {open && (
        <div className={`absolute z-30 mt-1 w-full overflow-hidden ${UI_DROPDOWN_MENU}`}>
          <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2 dark:border-white/[0.08]">
            <MdSearch className="h-4 w-4 text-gray-400" />
            <input
              value={effectiveTerm}
              onChange={(e) => {
                const v = e.target.value;
                if (onSearchChange) onSearchChange(v);
                else setLocalTerm(v);
              }}
              placeholder={placeholder}
              className="h-7 w-full border-0 bg-transparent text-sm outline-none dark:text-white"
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-3 text-center text-xs text-gray-500">...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-3 py-3 text-center text-xs text-gray-500">—</div>
            ) : (
              filteredOptions.map((opt) => {
                const selected = value.map(String).includes(String(opt.value));
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => toggle(opt.value)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-start text-sm hover:bg-gray-100 dark:hover:bg-white/10 ${
                      selected ? "bg-brand-50 dark:bg-brand-500/10" : ""
                    }`}
                  >
                    <span className="truncate">
                      <span className="font-medium text-gray-800 dark:text-white">{opt.label}</span>
                      {opt.description ? (
                        <span className="ml-2 text-xs text-gray-500">{opt.description}</span>
                      ) : null}
                    </span>
                    <span
                      className={`h-4 w-4 rounded border ${
                        selected
                          ? "border-brand-500 bg-brand-500"
                          : "border-gray-300 dark:border-white/20"
                      }`}
                    />
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
